import { ErrorCode } from '@utils/errorCodes';

import { shouldDropApolloTeardownInvariant } from './shouldDropApolloTeardownInvariant';
import { shouldDropHandledApollo401 } from './shouldDropHandledApollo401';
import { shouldDropHandledGraphql500 } from './shouldDropHandledGraphql500';
import { shouldDropHandledRegistration422 } from './shouldDropHandledRegistration422';
import { shouldDropIndexedDbTeardownRace } from './shouldDropIndexedDbTeardownRace';
import { shouldDropNetInfoReachabilityPing } from './shouldDropNetInfoReachabilityPing';

// Duck-typed, mirrors shouldDropHandledGraphql500's SentryEventLike — avoids
// importing @sentry/react-native's real Event type so tests can pass plain
// object literals with no SDK mocking.
type SentryEventLike = Parameters<typeof shouldDropHandledGraphql500>[0] &
  Parameters<typeof shouldDropHandledApollo401>[0] &
  Parameters<typeof shouldDropApolloTeardownInvariant>[0] &
  Parameters<typeof shouldDropHandledRegistration422>[0] &
  Parameters<typeof shouldDropIndexedDbTeardownRace>[0] &
  Parameters<typeof shouldDropNetInfoReachabilityPing>[0] & {
    tags?: Record<string, string | undefined>;
  };

// Error codes tagged on GraphQL errors (see useApolloClient.tsx's onError
// link, which sets tags.errorCode on every captured GraphQL exception) that
// represent expected business outcomes, not bugs — reporting them to Sentry
// causes alert fatigue (TA-10121 anti-pattern #5).
// PR #5782 review fix: useApolloClient.tsx's mutation-scoped-error comment
// already assumed SUBSCRIPTION_REQUIRED was in this list (it treats it as
// an expected business outcome alongside PERMISSION_DENIED, unlike CONFLICT
// which is deliberately monitored) — this was just missing here, so it
// still reached Sentry/Mixpanel as an "unexpected" error.
export const IGNORED_ERROR_CODES: string[] = [
  ErrorCode.RESOURCE_NOT_FOUND,
  ErrorCode.VALIDATION_FAILED,
  ErrorCode.PERMISSION_DENIED,
  ErrorCode.SUBSCRIPTION_REQUIRED,
];

// useApolloClient.tsx tags every captured GraphQL exception, falling back to
// this sentinel when the backend sent no `extensions.code`. Treating it as a
// real code made `shouldIgnoreSentryError` skip the legacy heuristic for
// every untagged-by-the-backend error — the opposite of the documented
// intent below — so normalise it back to "no code".
const UNKNOWN_ERROR_CODE = 'UNKNOWN';

const extractErrorCode = (event: SentryEventLike): string | undefined => {
  const errorCode = event.tags?.errorCode;
  return errorCode && errorCode !== UNKNOWN_ERROR_CODE ? errorCode : undefined;
};

/**
 * Shared Sentry `beforeSend` filter for both native (App.tsx) and web
 * (App.web.tsx). Drops events for expected business errors by code, and
 * falls back to the existing GraphQL-500 heuristic for events that predate
 * (or otherwise lack) the errorCode tag.
 */
export const shouldIgnoreSentryError = (event: SentryEventLike): boolean => {
  // Checked before the errorCode branch below: an HTTP 401 carries no GraphQL
  // response body, so it never gets an errorCode tag — but a tagged event is
  // treated as decided by that tag alone, and this must not depend on tagging
  // that the 401 path cannot produce. See shouldDropHandledApollo401.
  if (shouldDropHandledApollo401(event)) {
    return true;
  }

  // Same reason for running ahead of the errorCode branch: an Apollo teardown
  // invariant is raised client-side, so there is no GraphQL response to tag.
  if (shouldDropApolloTeardownInvariant(event)) {
    return true;
  }

  // A registration 422 is a plain REST response, not GraphQL — it never gets
  // an errorCode tag either, so this must also run ahead of that branch.
  if (shouldDropHandledRegistration422(event)) {
    return true;
  }

  // Also ahead of the errorCode branch, for the same reason: an IndexedDB
  // teardown race happens in the browser's storage layer, not in a GraphQL
  // response, so there is nothing for the onError link to tag it with.
  if (shouldDropIndexedDbTeardownRace(event)) {
    return true;
  }

  // Ahead of the errorCode branch for the same reason again: the reachability
  // ping is a plain HEAD to the origin root, not a GraphQL call, so the
  // onError link never sees it and there is no errorCode to tag it with.
  if (shouldDropNetInfoReachabilityPing(event)) {
    return true;
  }

  const errorCode = extractErrorCode(event);
  // PR #5782 review fix: this used to fall through to the legacy heuristic
  // even when errorCode was present but not ignored (e.g. INTERNAL_ERROR/
  // SERVICE_UNAVAILABLE/CONFLICT) — contradicting this function's own doc
  // comment above, which says the heuristic is only meant for events that
  // lack the errorCode tag. A tagged-but-not-ignored event could still get
  // dropped by the URL/status-code heuristic, hiding a real error the code
  // explicitly says to keep. Once a tag exists, it's the sole decision —
  // only fall back to the heuristic when there's no tag to go on.
  if (errorCode) {
    return IGNORED_ERROR_CODES.includes(errorCode);
  }

  return shouldDropHandledGraphql500(event);
};
